/**
 * Aba "Documentos" do Cliente 360. Grid filtrável de documentos reais do
 * cliente — agregados (read-only) de NF-e + contratos + anexos de orçamento/
 * lead via GET /clientes/{id}/documentos. Cada card abre o documento (PDF da
 * NF-e, link público do contrato, arquivo do anexo).
 *
 * A grade é o `DocumentoGrid` compartilhado (CRM-50): o mesmo desenho daqui
 * passou a valer no cockpit do lead. Esta aba continua só de leitura (sem
 * `onExcluir`), mas parou de engolir o erro da API — falha ao carregar
 * aparecia como "Nenhum documento".
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiFile, FiFileText, FiImage, FiInbox, FiLayers } from "react-icons/fi";

import DocumentoGrid from "../../../../components/DocumentoGrid";
import { obterDocumentosCliente } from "../../../../services/comercial";
import { Soon } from "../cli360";

const TIPO_VISUAL = {
  CONTRATO: { icon: <FiFileText size={20} aria-hidden="true" />, bg: "var(--accent-soft)", cor: "var(--accent)" },
  PROJETO: { icon: <FiLayers size={20} aria-hidden="true" />, bg: "var(--info-bg)", cor: "var(--info-text)" },
  ORCAMENTO: { icon: <FiFileText size={20} aria-hidden="true" />, bg: "var(--warning-bg)", cor: "var(--warning-text)" },
  FISCAL: { icon: <FiFileText size={20} aria-hidden="true" />, bg: "rgba(192,132,252,.14)", cor: "var(--chart-6)" },
  FOTO: { icon: <FiImage size={20} aria-hidden="true" />, bg: "rgba(52,211,153,.14)", cor: "var(--chart-2)" },
  OUTROS: { icon: <FiFile size={20} aria-hidden="true" />, bg: "var(--neutral-bg)", cor: "var(--neutral-text)" },
};

const FILTROS = [
  ["all", "Todos"],
  ["CONTRATO", "Contratos"],
  ["PROJETO", "Projetos"],
  ["ORCAMENTO", "Orçamentos"],
  ["FISCAL", "Fiscais"],
  ["OUTROS", "Outros"],
];

function DocumentosTab({ request, clienteId }) {
  const [docs, setDocs] = useState(null); // null = carregando
  const [erro, setErro] = useState(null);
  const [filtro, setFiltro] = useState("all");

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        // silentError: a mensagem aparece na própria aba, sem o toast em dobro.
        const data = await obterDocumentosCliente(request, clienteId, { silentError: true });
        if (vivo) {
          setDocs(Array.isArray(data?.documentos) ? data.documentos : []);
          setErro(null);
        }
      } catch (err) {
        if (vivo) {
          setDocs([]);
          setErro(err?.message || "Não foi possível carregar os documentos.");
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, [request, clienteId]);

  const filtrados = useMemo(() => {
    const lista = docs || [];
    if (filtro === "all") return lista;
    if (filtro === "OUTROS") return lista.filter((d) => d.tipo === "OUTROS" || d.tipo === "FOTO");
    return lista.filter((d) => d.tipo === filtro);
  }, [docs, filtro]);

  return (
    <section className="tab-panel">
      <div className="flex between center wrap" style={{ gap: 12, marginBottom: 16 }}>
        <div className="seg">
          {FILTROS.map(([k, l]) => (
            <button key={k} type="button" className={filtro === k ? "on" : ""} onClick={() => setFiltro(k)}>
              {l}
            </button>
          ))}
        </div>
        <span className="tiny muted">{(docs || []).length} documento(s)</span>
      </div>

      <DocumentoGrid
        documentos={filtrados}
        visualPorTipo={TIPO_VISUAL}
        carregando={docs === null}
        erro={erro}
        vazio={
          <div className="panel">
            <Soon icon={<FiInbox aria-hidden="true" />} title="Nenhum documento">
              {(docs || []).length === 0
                ? "Este cliente ainda não tem contratos, notas fiscais ou anexos. Eles aparecem aqui automaticamente conforme forem gerados."
                : "Nenhum documento neste filtro."}
            </Soon>
          </div>
        }
      />
    </section>
  );
}

export default DocumentosTab;
